import scssVariables from "@/assets/styles/variables.module.scss"
import { BaseTheme } from "@shared/theme"
import { embedFont } from "@/lib/fonts"
import { MVT_SELECTORS } from "@/lib/globals"

class PWATheme extends BaseTheme {
  constructor() {
    super()
    this.set("--card-shadow", scssVariables["box-shadow--small"])
  }

  insert() {
    const existingTheme = document.querySelector(
      `[data-mvt="${MVT_SELECTORS.THEME}"]`
    )

    if (existingTheme) {
      existingTheme.innerHTML = this.toString()
    } else {
      const el = document.createElement("style")
      el.type = "text/css"
      el.dataset.mvt = MVT_SELECTORS.THEME

      el.appendChild(document.createTextNode(this.toString()))
      document.head.appendChild(el)
    }
  }

  toString() {
    let css = ":root {\n"

    Object.keys(this.options).forEach(key => {
      css += `${key}: ${this.options[key]};\n`
    })

    css += "}"

    return css
  }

  rbgToVals(rgb) {
    return rgb.replace(/rgb\(/, "").replace(")", "")
  }

  applyThemeParams(themeParams) {
    super.applyThemeParams(themeParams, { scssVariables })

    if (themeParams.body_font) {
      embedFont(themeParams.body_font)
    }

    if (themeParams.heading_font) {
      embedFont(themeParams.heading_font)
    }
  }
}

// TIER_2_MOCK — temporary, delete with `shared/src/tier-2-mock-adapter.ts`.
// Stands up a tier-2 sheet from the legacy theme fields so components reading
// `--mvt-theme--*` respond to a theme change before the real generator ships.
// Loaded on its own so throwaway weight stays out of the entry chunks.
const insertMockTierTwo = async themeParams => {
  const {
    TIER_2_MOCK_ATTR,
    buildMockTierTwoTheme,
    hasRealTierTwo
  } = await import(
    /* webpackChunkName: "tier-2-mock" */ "@shared/tier-2-mock-adapter"
  )

  const existing = document.querySelector(`[${TIER_2_MOCK_ATTR}]`)

  if (hasRealTierTwo(themeParams.global_css)) {
    existing?.remove()
    return
  }

  const css = buildMockTierTwoTheme(themeParams)

  if (existing) {
    existing.innerHTML = css
    return
  }

  const el = document.createElement("style")
  el.type = "text/css"
  el.setAttribute(TIER_2_MOCK_ATTR, "")
  el.appendChild(document.createTextNode(css))
  document.head.appendChild(el)
}

export default themeParams => {
  const theme = new PWATheme()
  const { default_color_mode } = themeParams
  const isDark = default_color_mode === "dark"

  theme.applyThemeParams(themeParams)

  if (isDark) {
    document.body.classList.add("--dark-mode")
  } else {
    document.body.classList.remove("--dark-mode")
  }

  // TIER_2_MOCK — the mode selector tier 2 specifies (ADR 0006) is not the body
  // class the rest of the app switches on; delete alongside the mock.
  if (isDark) {
    document.documentElement.setAttribute("data-mvt-mode", "dark")
  } else {
    document.documentElement.removeAttribute("data-mvt-mode")
  }

  theme.insert()
  insertMockTierTwo(themeParams)
}
